import { Platform } from "react-native"
import StoreFront from "react-native-store-front"

import { IntegrationProvider, UserSubscription } from "@treefort/api-spec"
import { CheckoutError } from "@treefort/lib/errors"

import config from "../../config"
import { logError } from "../logging"

const PAYMENT_PROVIDERS = [
  "stripe",
  "appStore",
  "playStore",
  "webPayment",
  "groupMembership",
] as const

async function isPaymentFreedomGranted() {
  // Payment freedom is only allowed on iOS if the user is in the good ole US of
  // A
  if (config.PAYMENT_FREEDOM_GRANTED && Platform.OS === "ios") {
    try {
      const countryCode = await StoreFront.getStoreFront()
      return countryCode === "USA"
    } catch (cause) {
      logError(new CheckoutError("Couldn't get store front country", { cause }))
      return false
    }
  }
  return config.PAYMENT_FREEDOM_GRANTED
}

export async function canPurchasePlanFromProvider(
  provider: IntegrationProvider | undefined,
) {
  const paymentFreedomGranted = await isPaymentFreedomGranted()
  switch (provider) {
    case "stripe":
    case "webPayment":
    case "groupMembership":
      return paymentFreedomGranted
    case "appStore":
      return Platform.OS === "ios"
    case "playStore":
      return Platform.OS === "android"
    default:
      return false
  }
}

export async function canOpenBillingPortalForProvider(
  provider: IntegrationProvider | undefined,
  subscription: UserSubscription | undefined,
) {
  switch (provider) {
    case "stripe":
      // The Stripe billing portal will only work if the user has an active
      // subscription
      return (
        (await isPaymentFreedomGranted()) &&
        subscription !== undefined &&
        !subscription.deactivatedAt
      )
    case "webPayment":
      return isPaymentFreedomGranted()
    case "appStore":
      return Platform.OS === "ios" || Platform.OS === "web"
    case "playStore":
      return Platform.OS === "android" || Platform.OS === "web"
    default:
      return false
  }
}

export function canCancelPlanFromProvider(
  provider: IntegrationProvider | undefined,
) {
  switch (provider) {
    case "stripe":
    case "webPayment":
      // Only allow cancellation of web plans on platforms that support payment
      // freedom. We don't want to encourage users to shoot themselves in the
      // foot by moving to App Store or Play Store subscriptions which are
      // usually more expensive, harder to cancel, harder to refund, and are
      // just generally customer service nightmares
      return isPaymentFreedomGranted()
    case "playStore":
      // Cancelling Play Store subscriptions programatically via API has a
      // different effect than cancelling manually via the Play Store UI:
      // programatically canceled subscriptions cannot be uncanceled while
      // manually canceled subscriptions can via a "Resubscribe" button in the
      // Play Store UI. For that reason we push users towards the better "Manage
      // subscription" flow on Android instead of showing an in-app "Cancel
      // subscription" button. Everywhere else we show the cancel button as the
      // user is less likely to be signed into the Play Store's web UI with the
      // correct account and may have a hard time cancelling. Plus if the user
      // wants to resubscribe on another platform it probably won't be with a
      // Play Store subscription anyway.
      return Platform.OS !== "android"
    case "groupMembership":
      return true
    default:
      return false
  }
}

export async function getPaymentProvidersForPlatform() {
  const providers = await Promise.all(
    PAYMENT_PROVIDERS.map(async (provider) =>
      (await canPurchasePlanFromProvider(provider)) ? provider : null,
    ),
  )
  return providers.flatMap((provider) => (provider !== null ? [provider] : []))
}
